import { Component, inject, OnInit, ViewChild, effect } from '@angular/core';
import { PageHeaderComponent } from '../../../components/shared/page-header/page-header.component';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { TagModule } from 'primeng/tag';
import { MenuModule } from 'primeng/menu';
import { InputTextModule } from 'primeng/inputtext';
import { FormsModule } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { DrawerModule } from 'primeng/drawer';
import { TabsModule } from 'primeng/tabs';
import { Router } from '@angular/router';
import { EmailTraceService } from './email-trace.service';
import { getEmailTraceDataTableStructure } from './utils/email-trace-data-table-object';
import { DataTableRowActions, DynamicDataTable, DataTableServerQuery } from '../../../components/data-table-builder/data-table.types';
import { DataTableBuilderComponent } from '../../../components/data-table-builder/data-table-builder.component';
import { DialogObject } from '../../../components/dialog/dialog.types';
import { DialogComponent } from '../../../components/dialog/dialog.component';
import { MessageService } from 'primeng/api';
import { getEmailTraceResendDialogObject } from './utils/email-trace-resend-dialog-object';
import { getEmailTraceInspectDialogObject } from './utils/email-trace-inspect-dialog-object';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { EmailTraceRepository } from '@/store/email-trace-state/email-trace-state.repository';
import { EmailTrace, EmailTraceResponse } from '@/types/email-trace.types';
import { createServerQuery } from '../../../components/data-table-builder/server-query';

@Component({
  selector: 'sm-email-trace',
  imports: [
    PageHeaderComponent,
    CommonModule,
    ButtonModule,
    TableModule,
    IconFieldModule,
    InputIconModule,
    TagModule,
    MenuModule,
    InputTextModule,
    FormsModule,
    SelectModule,
    DrawerModule,
    TabsModule,
    DataTableBuilderComponent,
    DialogComponent,
    TranslateModule,
  ],
  templateUrl: './email-trace.component.html',
  styleUrl: './email-trace.component.scss',
})
export class EmailTraceComponent implements OnInit {
  emailTraceService = inject(EmailTraceService);
  emailTraceRepository = inject(EmailTraceRepository);
  messageService = inject(MessageService);
  translate = inject(TranslateService);
  router = inject(Router);

  //breadcrumb
  breadcrumb = [
    {
      label: 'settings.emailTrace.breadcrumb.parametres',
      routerLink: ['/settings'],
    },
    {
      label: 'settings.emailTrace.breadcrumb.emailTrace',
      routerLink: ['/settings', 'email-trace'],
    },
  ];

  @ViewChild('dialog') dialogComponent!: DialogComponent;
  emailTraceDialog?: DialogObject;

  serverQuery: DataTableServerQuery = createServerQuery({});

  rowActions: DataTableRowActions = {
    inspectAction: row => {
      this.emailTraceRepository.set('selectedEmailTrace', row);
      this.emailTraceDialog = getEmailTraceInspectDialogObject({
        t: this.translate,
        representation: row.objet,
        emailContent: row.contenu,
        toggleVisiblity: () => this.dialogComponent.toggleVisible(),
      });
      this.dialogComponent.toggleVisible();
    },
    resendAction: row => {
      this.emailTraceRepository.set('selectedEmailTrace', row);
      this.emailTraceDialog = getEmailTraceResendDialogObject({
        t: this.translate,
        representation: row.objet,
        action: () => {
          this.resendEmail(row.id);
          this.dialogComponent.toggleVisible();
        },
        toggleVisiblity: () => this.dialogComponent.toggleVisible(),
      });
      this.dialogComponent.toggleVisible();
    },
  };

  data: EmailTrace[] = [];
  totalRecords = 0;
  dataTableObject: DynamicDataTable = getEmailTraceDataTableStructure({
    store: this.emailTraceRepository,
    rowActions: this.rowActions,
    serverQuery: this.serverQuery,
  });
  loading = true;

  constructor() {
    effect(() => {
      this.serverQuery.page();
      this.serverQuery.pageSize();
      this.serverQuery.sortBy();
      this.serverQuery.sortOrder();
      this.serverQuery.search();
      this.loadData();
    });
  }

  ngOnInit(): void {
    this.loadData();
  }

  loadData() {
    this.loading = true;
    const params: any = {
      page: this.serverQuery.page(),
      size: this.serverQuery.pageSize(),
      sort: this.serverQuery.sortBy() ? `${this.serverQuery.sortBy()},${this.serverQuery.sortOrder()}` : '',
      search: this.serverQuery.search() || '',
    };

    this.emailTraceService.getEmailTraces(params).subscribe({
      next: (response: EmailTraceResponse) => {
        this.data = response.content;
        this.totalRecords = response.totalElements;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: this.translate.instant('settings.emailTrace.message.error.loading'),
        });
      },
    });
  }

  resendEmail(id: number) {
    this.loading = true;
    this.emailTraceService.resendEmail(id).subscribe({
      next: () => {
        this.loadData();
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: this.translate.instant('settings.emailTrace.message.success.resent'),
        });
      },
      error: error => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: error.error?.message || this.translate.instant('settings.emailTrace.message.error.resent'),
        });
      },
    });
  }
}
